/**
 * RevenueCat utilities for subscription verification.
 * Isolated in a separate file to avoid circular import dependencies.
 */
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Sentry } from './sentryConfig';
import { waitForPurchases } from './purchasesInit';

/**
 * Get the RevenueCat app user ID for server-side subscription verification.
 * Returns undefined on web platform or if RevenueCat is not initialized.
 *
 * Waits for Purchases.configure() to complete before calling getAppUserID()
 * to avoid race conditions where the SDK isn't ready yet.
 */
export const getRevenueCatAppUserId = async (): Promise<string | undefined> => {
  if (!Capacitor.isNativePlatform()) {
    return undefined;
  }

  try {
    // Ensure configure() has finished before calling any SDK method
    await waitForPurchases();
    const { appUserID } = await Purchases.getAppUserID();
    return appUserID;
  } catch (error) {
    console.error('[RevenueCat] Failed to get app user ID:', error);
    Sentry.captureException(error, {
      tags: { component: 'revenueCatUtils' },
      extra: { method: 'getRevenueCatAppUserId' },
    });
    return undefined;
  }
};
